/*
  CS-71: arrow paging for the native scroll rails that replaced Splide.

  Ported from CS-52, the testimonial band's arrow logic, which has been running since 8 August. The
  scrolling itself is entirely the browser's: this file only pages the rail from the two arrows and
  keeps their disabled state honest. Smoothness comes from `scroll-behavior` in the stylesheet, so a
  swipe is never animated by script.

  The arrows start hidden and are only revealed on a rail that actually overflows, so a collection
  trimmed to two products shows no arrows rather than two dead ones. Re-checked on resize, because a
  rail that overflows at 1280 may not at 1600.

  Everything here feature-detects and returns early, so a section without a rail costs one
  querySelectorAll and nothing else.
*/
(() => {
  const EPS = 2; // sub-pixel scroll positions never reach an exact maximum

  function wire(wrap) {
    // `init()` also runs on `shopify:section:load`, which re-scans the WHOLE document. The reloaded
    // section gets fresh DOM and wires cleanly, but every OTHER rail on the page keeps its elements
    // and would gain a second set of listeners and another ResizeObserver on each reload, so one
    // arrow click would page twice, then three times. Theme editor only, but that is exactly where
    // the carousels get tested. The flag lives on the element, so replaced DOM re-wires correctly.
    if (wrap.dataset.csRailWired) return;
    wrap.dataset.csRailWired = '1';

    const rail = wrap.querySelector('.cs-rail');
    const arrows = wrap.querySelector('[data-cs-rail-arrows]');
    if (!rail || !arrows) return;

    const prev = arrows.querySelector('[data-cs-rail-prev]');
    const next = arrows.querySelector('[data-cs-rail-next]');
    if (!prev || !next) return;

    function overflows() {
      return rail.scrollWidth - rail.clientWidth > EPS;
    }

    function sync() {
      // A rail that does not overflow has nothing to page, and two permanently dead arrows read as
      // broken. Hide the whole control rather than disable both.
      const live = overflows();
      arrows.hidden = !live;
      if (!live) return;
      const max = rail.scrollWidth - rail.clientWidth;
      prev.disabled = rail.scrollLeft <= EPS;
      next.disabled = rail.scrollLeft >= max - EPS;
    }

    function page(dir) {
      // 80% of a viewport, not 100%: a sliver of the next tile stays on screen so the reader keeps
      // their place. Same figure as the testimonial band.
      rail.scrollBy({ left: dir * Math.round(rail.clientWidth * 0.8), top: 0 });
    }

    prev.addEventListener('click', () => page(-1));
    next.addEventListener('click', () => page(1));
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Tiles are lazy-loaded, so scrollWidth grows after first paint: a rail measures as
    // non-overflowing at DOMContentLoaded and overflows a moment later.
    //
    // MEASURED FAILING: observing only `rail` left all eight arrow bars hidden on a page where all
    // eight rails overflowed. A ResizeObserver on the rail fires when the RAIL's own box changes,
    // and the rail is a full-width flex container whose box does not change when its CONTENT grows.
    // Observing the first tile as well catches exactly that, because the tiles are what resize as
    // their images arrive.
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(sync);
      ro.observe(rail);
      if (rail.firstElementChild) ro.observe(rail.firstElementChild);
    }

    // Belt and braces for the same race: `load` fires after every image has settled, and costs one
    // more sync on a page that is already idle.
    window.addEventListener('load', sync);

    sync();
  }

  function init() {
    document.querySelectorAll('[data-cs-rail]').forEach((wrap) => {
      try {
        wire(wrap);
      } catch (error) {
        /* ponytail: a rail we cannot wire still scrolls natively, which is the whole point. The
           arrows simply stay hidden. Never let one section throw and take the others with it. */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify re-renders a section when it is edited in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
